import { atom } from 'nanostores';
import { v4 } from 'uuid';
import { trackEvent } from '@/utils/analytics';

const Settings = {
  maxFileSize: 50 * 1024 * 1024,
  maxFloors: 50,
  acceptedExtensions: ['pdf', 'png', 'jpg', 'jpeg', 'dwg'],
  propertyTypes: ['apartment', 'house', 'other'],
  errors: {
    filesRequired: 'Upload at least one floor plan to continue.',
    type: 'Choose a PDF, PNG, JPG or DWG file.',
    empty: 'This file is empty. Choose another file.',
    size: 'Each floor plan must be 50 MB or smaller.',
    read: 'Could not read this file. Choose it again or upload another copy.',
    checking: 'Checking files… Please wait.',
    pdf: 'This PDF could not be opened. Export a new PDF or upload an image.',
    dwg: 'This DWG could not be opened. Export a new DWG, PDF or image.',
    image: 'This image could not be opened. Export it again or choose another file.',
    property: 'Choose Apartment, House or Other.',
    floors: 'Enter a whole number from 1 to 50.',
    total: 'This order has more than 50 floor plans. Reduce the floors or remove a file.',
  },
};

const en = {
  label: 'Upload floor plan',
  title: 'Upload floor plan',
};

const getExtension = (file) => file.name.split('.').pop()?.toLowerCase() || '';

export class Step1 {
  id = '1';

  locale = { en, ru: en };

  $files = atom([]);

  $filesError = atom('');

  $totalError = atom('');

  $isChecking = atom(false);

  validationId = 0;

  pendingChecks = 0;

  store;

  constructor(store) {
    this.store = store;
  }

  get floorsCount() {
    return this.$files.get().reduce((total, item) => total + (Number(item.floors) || 0), 0);
  }

  onEnter() {
    this.store.orderStore.step3.calculatePrice();
  }

  async onValidate() {
    if (this.$isChecking.get()) {
      this.$filesError.set(Settings.errors.checking);
      this.store.orderStore.focusField('files');
      return false;
    }

    const files = this.$files.get();
    if (!files.length) {
      this.$filesError.set(Settings.errors.filesRequired);
      this.store.orderStore.focusField('files');
      return false;
    }

    let firstInvalidField = '';
    const checkedFiles = files.map((item) => {
      let propertyError = '';
      let floorsError = '';
      if (!Settings.propertyTypes.includes(item.propertyType)) propertyError = Settings.errors.property;
      if (!Number.isInteger(Number(item.floors)) || Number(item.floors) < 1 || Number(item.floors) > Settings.maxFloors) {
        floorsError = Settings.errors.floors;
      }
      if (!firstInvalidField && (item.error || propertyError || floorsError)) firstInvalidField = item.id;
      return { ...item, propertyError, floorsError };
    });

    const totalError = this.floorsCount > Settings.maxFloors ? Settings.errors.total : '';
    this.$files.set(checkedFiles);
    this.$totalError.set(totalError);
    if (firstInvalidField || totalError) {
      this.store.orderStore.focusField(firstInvalidField || 'files-total');
      return false;
    }
    return true;
  }

  onTariffChange() {
    this.onReset();
  }

  onReset() {
    this.validationId += 1;
    this.pendingChecks = 0;
    this.$files.get().forEach(({ preview }) => {
      if (preview) URL.revokeObjectURL(preview);
    });
    this.$files.set([]);
    this.$filesError.set('');
    this.$totalError.set('');
    this.$isChecking.set(false);
  }

  async addFiles(filesArrayLike) {
    const incomingFiles = [...filesArrayLike];
    if (!incomingFiles.length) return;

    const { validationId } = this;
    this.pendingChecks += 1;
    this.$isChecking.set(true);
    this.$filesError.set(Settings.errors.checking);
    try {
      const checkedFiles = await Promise.all(incomingFiles.map((file) => this.createFileItem(file)));
      if (validationId !== this.validationId) {
        checkedFiles.forEach(({ preview }) => { if (preview) URL.revokeObjectURL(preview); });
        return;
      }
      this.$files.set([...this.$files.get(), ...checkedFiles]);
      this.updateTotalError();
      this.store.orderStore.scheduleDraftSave();
      this.store.orderStore.step3.calculatePrice();
      trackEvent('floorplan_upload_complete', {
        file_count: checkedFiles.filter((item) => !item.error).length,
        file_type: [...new Set(incomingFiles.map((file) => file.type || getExtension(file)))].join(','),
      });
    } catch (error) {
      if (validationId === this.validationId) this.$filesError.set(Settings.errors.read);
    } finally {
      if (validationId === this.validationId) {
        this.pendingChecks -= 1;
        this.$isChecking.set(this.pendingChecks > 0);
        if (!this.pendingChecks && this.$filesError.get() === Settings.errors.checking) this.$filesError.set('');
      }
    }
  }

  async createFileItem(file) {
    const extension = getExtension(file);
    let error = '';
    if (!Settings.acceptedExtensions.includes(extension)) error = Settings.errors.type;
    else if (file.size === 0) error = Settings.errors.empty;
    else if (file.size > Settings.maxFileSize) error = Settings.errors.size;
    else error = await this.validateFileContent(file, extension);

    const hasPreview = ['png', 'jpg', 'jpeg'].includes(extension) && !error;
    return {
      id: v4(),
      file,
      preview: hasPreview ? URL.createObjectURL(file) : '',
      propertyType: '',
      floors: 1,
      error,
      propertyError: '',
      floorsError: '',
    };
  }

  async validateFileContent(file, extension) {
    try {
      if (extension === 'pdf' || extension === 'dwg') {
        const header = new Uint8Array(await file.slice(0, 8).arrayBuffer());
        const signature = String.fromCharCode(...header);
        if (extension === 'pdf' && !signature.startsWith('%PDF-')) return Settings.errors.pdf;
        if (extension === 'dwg' && !/^AC10\d{2}/.test(signature)) return Settings.errors.dwg;
      }
      if (['png', 'jpg', 'jpeg'].includes(extension)) {
        const bitmap = await window.createImageBitmap(file);
        bitmap.close();
      }
      return '';
    } catch (error) {
      if (extension === 'pdf') return Settings.errors.pdf;
      if (extension === 'dwg') return Settings.errors.dwg;
      if (['png', 'jpg', 'jpeg'].includes(extension)) return Settings.errors.image;
      return Settings.errors.read;
    }
  }

  removeFile(id) {
    const item = this.$files.get().find((fileItem) => fileItem.id === id);
    if (item?.preview) URL.revokeObjectURL(item.preview);
    this.$files.set(this.$files.get().filter((fileItem) => fileItem.id !== id));
    this.updateTotalError();
    this.store.orderStore.scheduleDraftSave();
    this.store.orderStore.step3.calculatePrice();
  }

  setPropertyType(id, propertyType) {
    this.updateFile(id, { propertyType, propertyError: '' });
  }

  setFloors(id, value) {
    this.updateFile(id, { floors: value, floorsError: '' });
    this.updateTotalError();
    this.store.orderStore.step3.calculatePrice();
  }

  updateFile(id, patch) {
    this.$files.set(this.$files.get().map((item) => (item.id === id ? { ...item, ...patch } : item)));
    this.store.orderStore.scheduleDraftSave();
  }

  updateTotalError() {
    this.$totalError.set(this.floorsCount > Settings.maxFloors ? Settings.errors.total : '');
  }

  getDraftData() {
    return {
      files: this.$files.get().map((item) => ({
        id: item.id,
        file: item.file,
        propertyType: item.propertyType,
        floors: item.floors,
      })),
    };
  }

  async restoreDraft(data = {}) {
    this.onReset();
    const { validationId } = this;
    this.$isChecking.set(true);
    try {
      const files = await Promise.all((data.files || []).map(async (item) => ({
        ...await this.createFileItem(item.file),
        propertyType: item.propertyType,
        floors: item.floors,
      })));
      if (validationId !== this.validationId) {
        files.forEach(({ preview }) => { if (preview) URL.revokeObjectURL(preview); });
        return;
      }
      this.$files.set(files);
      this.updateTotalError();
    } finally {
      if (validationId === this.validationId) this.$isChecking.set(false);
    }
  }
}
